import { Component, DestroyRef, inject, Input, signal, ChangeDetectionStrategy } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { translate, TranslocoModule } from '@jsverse/transloco';
import { map } from 'rxjs/operators';
import { ConfigService } from '../../config/config.service';
import { SessionService } from '../../../app/services/session.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationDialogComponent } from '../../../app/components/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FormatUtil } from '../../../app/services/FormatUtil';
import { Design, SubDomainDesignService } from '@eadsysteme/portalsettingclient-v1';

@Component({
	selector: 'vex-header',
	imports: [MatButtonModule, MatIconModule, RouterModule, TranslocoModule],
	templateUrl: './header.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './header.component.scss',
})
export class HeaderComponent {
	configService = inject(ConfigService);
	sessionService = inject(SessionService);
	subDomainDesignService = inject(SubDomainDesignService);
	dialog = inject(MatDialog);
	destroyRef = inject(DestroyRef);

	imageUrl$ = this.configService.config$.pipe(map((config) => config.sidenav.imageUrl));
	readonly urlPattern = /^[\w-]+\.[\w-]+\.$/;

	legalNoticeText = '';
	privacyText = '';
	groupName = '';
	welcomeText = '';
	isExpanded = signal(true);

	@Input() primaryColor = '';
	logo: any;

	constructor() {
		let domain: string = globalThis?.location?.host ?? '';
		domain = domain.slice(0, domain.lastIndexOf('.')) + '.';
		if (domain.includes('localhost') || !this.urlPattern.test(domain)) {
			domain = 'portal.ead.';
		}
		this.subDomainDesignService
			.getDomainDesign(0, domain)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (design: Design) => {
					const attributes = design?.attributes;
					const logoBase64 =
						design.blobAttributes?.find((value) => value.name === 'logoLightMode')?.value ??
						design.blobAttributes?.find((value) => value.name === 'logo')?.value;
					this.logo = logoBase64 ? `data:image/png;base64,${logoBase64}` : '';
					this.primaryColor = attributes?.find((value) => value.name === 'primaryColor')?.value;
					this.legalNoticeText = FormatUtil.stripWhitespace(attributes?.find((value) => value.name === 'legalNotice')?.value);
					this.privacyText = FormatUtil.stripWhitespace(attributes?.find((value) => value.name === 'privacy')?.value);
					this.groupName = attributes?.find((value) => value.name === 'groupName')?.value;
					this.welcomeText = attributes?.find((value) => value.name === 'araWebWelcomeMessage')?.value;
				},
			});
	}

	onMouseEnter(): void {
		this.isExpanded.set(true);
	}

	onMouseLeave(): void {
		this.isExpanded.set(false);
	}

	protected showLegalNotice() {
		this.showDialog('legal');
	}

	protected showPrivacy() {
		this.showDialog('privacy');
	}

	private showDialog(type: 'privacy' | 'legal') {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: true,
			autoFocus: false,
			restoreFocus: false,
			maxWidth: '950px',
		});
		dialogRef.componentInstance.closeOnEscapeKey = true;
		dialogRef.componentInstance.showConfirmButton = false;
		dialogRef.componentInstance.closeButtonText = translate('commonActions.close');

		switch (type) {
			case 'privacy':
				dialogRef.componentInstance.titleMessage = translate('footer.privacy');
				dialogRef.componentInstance.confirmMessage = translate(this.privacyText);
				break;
			case 'legal':
				dialogRef.componentInstance.titleMessage = translate('footer.legalNotice');
				dialogRef.componentInstance.confirmMessage = translate(this.legalNoticeText);
				break;
			default:
				break;
		}
	}
}
